export const SUBTITLE_SIZE_SCALE = 0.8;
/**
 * Размер субтитров из настроек поверх SUBTITLE_SIZE_SCALE: множитель кегля и доли полосы
 * субтитров, которую может занять текст. По ширине текст всё так же не выходит за
 * безопасную зону, «Средний» совпадает с прежним размером.
 */
export const SUBTITLE_SIZE_FACTORS = { small: 0.85, medium: 1, large: 1.2 } as const;
/** Общие переносы, акценты и время печати для рендера и предпросмотра. */
/** Normalized from the supplied social UI mask, with extra space for outlines/shadows.
 * These are product layout bounds, not a guarantee for every platform UI variant. */
export const SOCIAL_SAFE_AREA = { left: 0.035, right: 0.95, top: 0.125, bottom: 0.835,
  rightRailTop: 0.63, lowerRight: 0.865, padding: 0.02 } as const;
/**
 * Где стоят субтитры: 'top' и 'bottom' — готовые положения в безопасной зоне соцсетей,
 * число от 0 до 1 — ручное положение (0 — у верхнего края кадра, 1 — у нижнего).
 */
export type SubtitlePosition = 'top' | 'bottom' | number;
const BAND_HEIGHT = 0.16;
/** Верх полосы субтитров для готовых положений и пределы ручного, доли высоты кадра. */
export const SUBTITLE_TRACK = {
  bandHeight: BAND_HEIGHT,
  presetTop: SOCIAL_SAFE_AREA.top + SOCIAL_SAFE_AREA.padding,
  presetBottom: SOCIAL_SAFE_AREA.bottom - SOCIAL_SAFE_AREA.padding - BAND_HEIGHT,
  // Вручную полосу можно поставить почти к краю кадра: остаётся отступ 3%.
  minTop: 0.03,
  maxTop: 1 - 0.03 - BAND_HEIGHT,
} as const;
/** Верх полосы субтитров, доля высоты кадра. */
function bandTop(position: SubtitlePosition | undefined): number {
  if (position === 'top') return SUBTITLE_TRACK.presetTop;
  if (position === 'bottom' || position === undefined || !Number.isFinite(position)) return SUBTITLE_TRACK.presetBottom;
  return SUBTITLE_TRACK.minTop + (SUBTITLE_TRACK.maxTop - SUBTITLE_TRACK.minTop) * Math.max(0, Math.min(1, position));
}
/** Ручное положение (0…1), при котором полоса стоит там же, где сейчас. */
export function subtitlePlacement(position: SubtitlePosition | undefined): number {
  return Math.max(0, Math.min(1, (bandTop(position) - SUBTITLE_TRACK.minTop) / (SUBTITLE_TRACK.maxTop - SUBTITLE_TRACK.minTop)));
}
/** Субтитры в верхней половине кадра — хук уходит вниз, и наоборот. */
export function subtitlesInUpperHalf(position: SubtitlePosition | undefined): boolean {
  return bandTop(position) + BAND_HEIGHT / 2 < 0.5;
}
const layoutFor = (top: number) => {
  const bandHeight = BAND_HEIGHT;
  const left = 0.08;
  const right = Math.min(0.92, (top + bandHeight >= SOCIAL_SAFE_AREA.rightRailTop
    ? SOCIAL_SAFE_AREA.lowerRight : SOCIAL_SAFE_AREA.right) - SOCIAL_SAFE_AREA.padding);
  return { top, left, right, centerX: (left + right) / 2, centerY: top + bandHeight / 2,
    bandHeight, bottom: 1 - top - bandHeight };
};
export const SUBTITLE_LAYOUT = layoutFor(SUBTITLE_TRACK.presetBottom);
export function subtitleLayout(position: SubtitlePosition = 'bottom') {
  return layoutFor(bandTop(position));
}

/**
 * Полоса хука: напротив субтитров, без субтитров — сверху. При ручном положении субтитров
 * хук тоже можно поставить вручную (`hookPosition`, 0…1 в тех же пределах).
 */
export function hookLayout(position: SubtitlePosition = 'bottom', subtitlesEnabled = true, hookPosition?: number | null) {
  if (typeof position === 'number' && typeof hookPosition === 'number') return subtitleLayout(hookPosition);
  return subtitleLayout(subtitlesEnabled && subtitlesInUpperHalf(position) ? 'bottom' : 'top');
}
export function hookCenterY(position: SubtitlePosition = 'bottom', subtitlesEnabled = true, hookPosition?: number | null): number {
  return hookLayout(position, subtitlesEnabled, hookPosition).centerY;
}

/** Первая буква фразы заглавная («Минимал»), остальное как в расшифровке; кавычки и скобки пропускаются. */
export function capitalizeFirst(text: string): string {
  return text.replace(/\p{L}/u, letter => letter.toLocaleUpperCase('ru-RU'));
}

export const DYNAMIC_SUBTITLE_OPTIONS = { maxWords: 3, maxChars: 24, maxDurationS: 1.6 } as const;
const NUMBER = /^\d+(?:[.,]\d+)?(?:[%₽$€])?$/u;
const QUANTITY = /^(?:более|менее|около|почти|примерно|свыше|ровно|всего|over|about)$/iu;
const UNIT = /^(?:лет|год(?:а|ов)?|месяц(?:а|ев)?|дн(?:я|ей)|день|час(?:а|ов)?|минут(?:а|ы)?|секунд(?:а|ы)?|раз(?:а)?|процент(?:а|ов)?|рубл(?:ь|я|ей)|доллар(?:а|ов)?|тысяч(?:а|и)?|миллион(?:а|ов)?|кг|км|см|м|years?|months?|days?|hours?|minutes?|seconds?|times|percent)$/iu;
const bareWord = (text: string) => text.replace(/^[«“"(]+|[.,!?…;:»”")]+$/gu, '');

/** A number and its unit are one meaning: “9 лет”, “25 процентов”. */
export function bindsPair(left: string, right: string): boolean {
  return bindsToNext(left) || (NUMBER.test(bareWord(left)) && UNIT.test(bareWord(right)))
    || (QUANTITY.test(left) && NUMBER.test(bareWord(right)));
}

// Do not strand a connective or a short pronoun at a visual line/page boundary.
const BIND_FORWARD = new Set('а и но да или либо что чтобы как в во на с со к ко у о об обо от до за для по из без при про над под перед через не ни я ты он мы вы not no the a an and or but to of in on for with i'.split(' '));
export function bindsToNext(text: string): boolean {
  // Punctuation closes a thought; “я.” must not join the next sentence.
  return !/[,;:.!?…»”"\)\]]$/.test(text) && BIND_FORWARD.has(text.toLowerCase().replace(/^[«“"\(\[]+/u, ''));
}

/** Groups that may wrap as a whole, including chains such as “и я в доме”. */
export function subtitleGroups<T extends { text: string }>(words: readonly T[]): T[][] {
  const groups: T[][] = [];
  for (const word of words) {
    const previous = groups.at(-1);
    if (previous && bindsPair(previous.at(-1)!.text, word.text)) previous.push(word);
    else groups.push([word]);
  }
  return groups;
}

export function accentStart(words: readonly { text: string }[], hot: number): number {
  let start = hot;
  while (start > 0 && bindsToNext(words[start - 1]!.text)) start--;
  return start;
}

export function accentEnd(words: readonly { text: string }[], hot: number): number {
  let end = hot;
  while (end >= 0 && end < words.length - 1 && bindsPair(words[end]!.text, words[end + 1]!.text)) end++;
  return end;
}

export interface SubtitleWord { text: string; tlStart: number; tlEnd: number }
export interface SubtitleCue { text: string; at: number }

/** Corrected multiword tokens retain their original interval, in reading order. */
export function subtitleWords<T extends SubtitleWord>(words: readonly T[]): T[] {
  return words.flatMap(word => {
    const parts = word.text.trim().split(/\s+/u).filter(Boolean);
    if (parts.length === 1) return [{ ...word, text: parts[0]! }];
    const total = parts.reduce((sum, text) => sum + text.length, 0);
    let offset = 0;
    return parts.map(text => {
      const tlStart = word.tlStart + (word.tlEnd - word.tlStart) * offset / total;
      offset += text.length;
      return { ...word, text, tlStart, tlEnd: word.tlStart + (word.tlEnd - word.tlStart) * offset / total };
    });
  });
}

/** Reveal the complete word at its speech onset, retaining the phrase layout. */
export function subtitleWordCue(word: SubtitleWord): SubtitleCue {
  return { text: word.text, at: word.tlStart };
}

const STOP = new Set('и а но да или что это вот уже ещё еще как так то же бы ли не ни на в во с со к ко у о об от до за для по из без при про я ты он она мы вы они его ее их мне вам наш ваш этот эта эти есть был быть the a an and or but is are to of in on for it this that with you i we'.split(' '));
const EMPHASIS = /^(важн|главн|ошиб|нужн|нельзя|никогда|всегда|перв|лучш|реша|секрет|почему|результ|просто|только|important|never|always|mistake|first|best|secret|why|result)/i;

export function accentScore(text: string): number {
  const word = text.toLocaleLowerCase().replace(/[^\p{L}\p{N}%]/gu, '');
  if (!word || STOP.has(word)) return -1;
  return Math.min(word.length, 12) / 12 + (/\d/.test(word) ? 2 : 0) + (EMPHASIS.test(word) ? 1.5 : 0);
}

export function accentIndex(words: readonly { text: string }[]): number {
  let best = 0;
  for (let i = 1; i < words.length; i++) {
    if (accentScore(words[i]!.text) > accentScore(words[best]!.text)) best = i;
  }
  return best;
}

/** Frequent accents for short captions, without emphasizing filler-only phrases. */
export function captionAccentIndex(words: readonly { text: string }[]): number {
  const index = accentIndex(words);
  return words[index] && accentScore(words[index]!.text) > 0 ? index : -1;
}
